// What one bracket slot may be fed by, and which group leads the menu.
//
// One comparison decides everything: a slot may only take a game in a LOWER
// bracket_round than its own. That is the relevance filter (a Semifinal slot
// leads with the Quarterfinals) and the loop guard (nothing in the same or a
// later round can ever be offered, so a cycle cannot be built).
//
// Rows arrive under two spellings — the grid rows carry `bracket_round`, the
// slot list carries `round` — so the round is read through one helper.

export const roundOf = (x: any) => Number(x?.bracket_round ?? x?.round ?? 1) || 1;

/** The name a round goes by, taken from the labels of the games in it. */
export const roundWords = (games: any[], round: number) => {
  const counts = new Map<string, number>();
  for (const x of games.filter((g) => roundOf(g) === round)) {
    const w = String(x.words ?? x.bracket_label ?? "").trim();
    if (w) counts.set(w, (counts.get(w) ?? 0) + 1);
  }
  let best = "";
  let top = 0;
  for (const [w, n] of counts) if (n > top) { best = w; top = n; }
  return best || `Round ${round}`;
};

/** A 3rd-place or consolation game takes losers, so its list leads with them. */
export const takesLosers = (g: any) =>
  String(g?.game_type) === "consolation"
  || String(g?.source_a_type) === "loser_of"
  || String(g?.source_b_type) === "loser_of"
  || /consolation|3rd|third place|placement|loser/i.test(String(g?.bracket_label ?? ""));

export type SlotOptions = {
  round: number;
  /** Every game that may legally feed this slot: lower rounds only. */
  earlier: any[];
  /** The nearest lower round with games, and the games in it. */
  prevRound: number;
  prev: any[];
  prevName: string;
  losersFirst: boolean;
  hasEarlier: boolean;
  moreThanPrev: boolean;
};

export function slotOptions(g: any, bracketGames: any[]): SlotOptions {
  const round = roundOf(g);
  const earlier = (bracketGames ?? []).filter(
    (x: any) => String(x.id) !== String(g.id) && roundOf(x) < round,
  );
  const prevRound = earlier.reduce((m: number, x: any) => Math.max(m, roundOf(x)), 0);
  const prev = earlier.filter((x: any) => roundOf(x) === prevRound);
  return {
    round,
    earlier,
    prevRound,
    prev,
    prevName: prevRound ? roundWords(bracketGames ?? [], prevRound) : "",
    losersFirst: takesLosers(g),
    hasEarlier: earlier.length > 0,
    moreThanPrev: earlier.length > prev.length,
  };
}

/**
 * The games in OTHER brackets of the same division, grouped by bracket.
 *
 * A consolation bracket runs on the losers of the main one, so its round 1 has
 * to be able to reach Winners round 1 — a round number in another bracket says
 * nothing about this one, and the round rule above must not be applied across
 * brackets. The loop guard for these is the feed graph, checked on save.
 */
export type BracketGroup = { id: string; name: string; games: any[] };

export function crossBracketGroups(
  g: any,
  bracketGames: any[],
  brackets: Array<{ id: string; name: string }> = [],
): BracketGroup[] {
  const mine = String(g?.bracket_id ?? "");
  const nameOf = new Map(brackets.map((b) => [String(b.id), b.name]));
  const order = brackets.map((b) => String(b.id));
  const byBracket = new Map<string, any[]>();
  for (const x of bracketGames ?? []) {
    const bid = String(x.bracket_id ?? "");
    if (!bid || bid === mine) continue;
    if (String(x.id) === String(g?.id)) continue;
    const arr = byBracket.get(bid) ?? [];
    arr.push(x);
    byBracket.set(bid, arr);
  }
  return [...byBracket.entries()]
    .sort((a, b) => (order.indexOf(a[0]) + 1 || 99) - (order.indexOf(b[0]) + 1 || 99))
    .map(([id, games]) => ({
      id,
      name: nameOf.get(id) ?? "Another bracket",
      games: games.slice().sort((a, b) => (a.game_number ?? 1e9) - (b.game_number ?? 1e9)),
    }));
}

